<script setup lang="ts">
import { computed } from 'vue'
import { RouterView, useRoute } from 'vue-router'

import AppSplash from '@/components/system/AppSplash.vue'
import ErrorBoundary from '@/components/system/ErrorBoundary.vue'
import RouteAnnouncer from '@/components/system/RouteAnnouncer.vue'
import ToastRegion from '@/components/system/ToastRegion.vue'
import AppLayout from '@/layouts/AppLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'

/**
 * The layout switch.
 *
 * `route.meta.layout` rather than nested route records: nesting would put the shell in the
 * parent route's component, and a layout change would then remount every child — losing
 * scroll position and in-flight queries on a navigation that only changed the chrome.
 *
 * Defaults to `auth`, the layout with no navigation and no operator identity in it. The
 * fail-safe points this way on purpose: a route that forgets `meta.layout` renders the
 * shell-less page rather than a shell built from a session that may not exist.
 */
const route = useRoute()

const layout = computed(() => (route.meta.layout === 'app' ? AppLayout : AuthLayout))

/**
 * No navigation has resolved yet — the splash window.
 *
 * `matched` is empty on exactly one route: vue-router's `START_LOCATION`, which is what
 * `useRoute()` returns from mount until the first navigation is *confirmed*. That is the
 * window the sign-in flash lived in. `meta` is `{}` there, so `layout` fell through to its
 * `auth` default and every refresh of `/shops` painted the credential shell — brand panel,
 * assurances and all — for the length of `/admin/auth/me`, then swapped to the panel. It
 * read as being signed out and straight back in.
 *
 * **Not `!auth.ready`**, which is the obvious condition and is wrong. `reset()` sets
 * `ready` false mid-session — `ChangePasswordView` calls it before navigating to login —
 * so the splash would cover the login screen for a round trip after every password
 * change. `ready` answers "has the session been read"; this asks "is there a screen yet",
 * and only the second one is what the splash is for.
 *
 * It needs no `auth` import at all, which is the other half of why this is the right
 * signal: the splash covers a slow route chunk on the same frame, with no second rule.
 */
const booting = computed(() => route.matched.length === 0)
</script>

<template>
  <!--
    The splash sits OVER the shell rather than beside it, and leaves on a fade.

    It is `fixed inset-0 z-50`, so by the time it starts fading the real screen is already
    painted underneath and the fade reveals it. The alternative — swapping one for the
    other — gives a frame of empty canvas between them, which is the flicker this whole
    change exists to remove.
  -->
  <Transition name="splash">
    <AppSplash v-if="booting" />
  </Transition>

  <!--
    The shell is not rendered at all while booting, rather than rendered and covered.
    `layout` falls through to `AuthLayout` on `START_LOCATION`, and mounting the credential
    screen's brand panel just to hide it behind the splash is work whose only possible
    outcome is a flash if the z-index is ever wrong.
  -->
  <template v-if="!booting">
    <!--
      The skip link, first in the DOM and visible only on focus. A dense operator tool puts
      a lot of chrome before its content, and a keyboard user should not tab through all of
      it on every navigation.
    -->
    <a
      href="#main"
      class="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-control focus:bg-action focus:px-3 focus:py-2 focus:text-white"
    >
      Skip to content
    </a>

    <RouteAnnouncer />

    <component :is="layout">
      <!--
        Keyed on `path`, **never `fullPath`**.

        `fullPath` carries the query string, so every keystroke in a filter — which writes
        the value to the URL — changed the key and remounted the whole screen. The input the
        operator was typing into was destroyed and recreated between characters, so focus
        landed on the body and they had to click the field again for every letter.

        The old key existed to stop a stale list showing under new filters. That is the data
        layer's job and it already does it: every list query sets `placeholderData:
        keepPreviousData`, which holds the previous rows until the new page arrives. Keying
        on `path` still gives a real navigation a fresh view and a fresh error boundary,
        because a different screen has a different path.
      -->
      <!--
        ARRIVAL ONLY, as a mount animation rather than a `<Transition>`.

        There is deliberately nothing on the way out: a leave holds the outgoing screen
        while it fades, adding its whole duration to every navigation — the commonest way
        an app reaching for polish ends up feeling slow.

        Which is exactly why this is an animation and not a transition. With a
        `<Transition>` the leave classes applied and the enter never ran — measured, no
        `transitionstart` on the incoming view — because enter classes land on the node
        *at insertion*, and this screen's root arrives from a lazily-imported chunk. A
        keyframe on mount has no such choreography to get wrong: the element exists, so it
        animates. `ErrorBoundary` is keyed on the path, so it remounts per route and the
        animation replays.
      -->
      <RouterView v-slot="{ Component }">
        <ErrorBoundary :key="route.path">
          <component
            :is="Component"
            id="main"
            class="app-view-in"
          />
        </ErrorBoundary>
      </RouterView>
    </component>

  </template>

  <!--
    OUTSIDE the `v-if`, unlike the rest of the shell.

    `bootstrap.ts` reports a failed navigation — a dead backend, a stale chunk — through
    `router.onError`, which raises a toast. Rendering the toast region only once a route
    had resolved meant exactly the errors that PREVENT a route resolving had nowhere to
    appear: the one case where the operator is staring at a screen with no explanation on
    it was the one case the explanation was suppressed.
  -->
  <ToastRegion />
</template>

<style scoped>
/*
  Leave only. There is no enter transition and that is deliberate: the splash is the first
  thing painted, so fading it IN would mean starting the session check behind a blank
  screen — the exact blankness it exists to replace.
*/
.splash-leave-active {
  transition: opacity var(--motion-overlay) var(--motion-ease);
}
.splash-leave-to {
  opacity: 0;
}

</style>

<!--
  UNSCOPED, and that is the point.

  The class rides on `<component :is>` through `ErrorBoundary`'s slot onto a lazily
  imported view's root node — and that node never receives this file's `data-v-` scope
  attribute, so a scoped `.view-in[data-v-…]` matched nothing. Measured: the element had
  `class` and `id` and no scope attribute at all, with `animation-name: none`.

  Prefixed `app-` because an unscoped name is a global one.
-->
<style>
/*
  A screen arriving. 3px and 160ms — the smallest movement that still reads as "this is a
  new page" rather than a repaint, and short enough that an operator clicking through a
  list never waits on it. Arrival only; nothing animates on the way out.
*/
@keyframes app-view-in {
  from {
    opacity: 0;
    transform: translateY(3px);
  }
}

.app-view-in {
  animation: app-view-in var(--motion-overlay) var(--motion-ease-out);
}

@media (prefers-reduced-motion: reduce) {
  .app-view-in {
    animation: none;
  }
}
</style>
